import React, { useState } from "react";

import {
  DetailDisplay,
  Dialog,
  ErrorButton,
  ErrorMessage,
  PrimaryButton,
  Table,
  Text,
} from "widgets";

import {
  BUSINESS_VERTICALS,
  DEFAULT_COUNTRY_NAME,
  INR_SYMBOL,
  KH_VERTICAL_ID,
  LOAN_PAYMENT_METHOD,
  ORDER_STATUSES,
  PACKAGE_DETAILS_COLUMN,
  PART_PAYMENT_DETAILS_COLUMN,
  PAYMENT_DETAILS_COLUMN,
  PAYMENT_STATUSES,
  PAYMENTS_ATTEMPTS_COLUMN,
  PENDING_ORDER_STATUS,
  REFUND_DETAILS_COLUMN,
} from "constants/config";
import DiscardOrder from "components/DiscardOrder/DiscardOrder";
import CancelOrder from "components/CancelOrder/CancelOrder";
import {
  useFetchOrderSummaryPDF,
  useFetchPaymentReceipt,
  useFetchTaxInvoicePDF,
} from "store/backend/assets";
import Loader from "Shared/Loader/Loader";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CourseEnroll from "components/CourseEnroll/CourseEnroll";
import DownloadInvoice from "components/DownloadInvoice/DownloadInvoice";

import styles from "./CustomerOrderDetails.module.scss";

const getFormattedPackageDetails = (orderDetail) => {
  if (!orderDetail || !Array.isArray(orderDetail.packages)) {
    return [];
  }
  return orderDetail.packages.flatMap((pkg) => {
    const subscriptions = pkg.subscriptions || [];
    return subscriptions.map((sub) => {
      const grade =
        pkg.package_grades?.map((grade) => grade.grade_name).join(", ") ||
        "N/A";
      return {
        name: pkg.display_name || "N/A",
        price: INR_SYMBOL + (sub.mrp || 0).toLocaleString("en-IN"),
        discount:
          INR_SYMBOL +
          (sub.discount || [])
            .reduce((total, d) => total + d.amount, 0)
            .toLocaleString("en-IN"),
        duration: sub.no_of_days || 0,
        grade: grade,
        hasPaymentHead:
          Array.isArray(sub.payment_heads) && sub.payment_heads.length > 0,
      };
    });
  });
};

const getFormattedRefundDetails = (refundDetails) => {
  if (!refundDetails || !Array.isArray(refundDetails)) {
    return [];
  }
  return refundDetails.map((refundDetail) => {
    return {
      amount: refundDetail.inr_conversion.toLocaleString("en-IN"),
      status: refundDetail.status?.name,
      refundDate: new Date(refundDetail.refund_date).toLocaleString(),
      refundReason: refundDetail.reason.name,
    };
  });
};

const getFormattedPartPaymentDetails = (partPaymentDetails) => {
  if (!partPaymentDetails || !Array.isArray(partPaymentDetails)) {
    return [];
  }

  return partPaymentDetails.flatMap((payment) =>
    payment.installments.map((installment) => {

      return {
        installmentNo: installment.installment_no,
        amount: installment.amount.toLocaleString("en-IN"),
        amountPaid: installment.amount_paid.toLocaleString("en-IN"),
        status: installment.status.name,
        dueDate: installment.due_date,
      };
    })
  );
};

const handleOnClickDownloadPaymentReceipt = (
  paymentId,
  fetchPaymentReceipt,
  openSnackbar
) => {
  if (paymentId) {
    fetchPaymentReceipt(paymentId)
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.signedUrl;
        link.click();
        openSnackbar("Payment receipt downloaded successfully!", "success");
      })
      .catch((error) => {
        console.error("Error fetching receipt", error);
        openSnackbar(
          error.message || "Error fetching payment receipt",
          "error"
        );
      });
  } else {
    console.error("No payment ID provided");
  }
};

const handleDialogClose = (setOpenDialogContent) => () => {
  setOpenDialogContent(null);
};

const handleDialogOpen =
  (setOpenDialogContent, content, size = {}) =>
  () => {
    setOpenDialogContent({ content, size });
  };

const getFormattedPaymentDetails = (
  selectedOrder,
  fetchPaymentReceipt,
  isFetchPaymentReceiptLoading,
  openSnackbar,
  setOpenDialogContent
) => {
  if (!selectedOrder || !Array.isArray(selectedOrder.payment_details))
    return [];

  return selectedOrder.payment_details
    .sort((p1, p2) => new Date(p2.date) - new Date(p1.date))
    .map((paymentDetail) => {
      const paymentStatus = paymentDetail.status.id;

      const paymentMethod = paymentDetail.method.id;

      const canDownloadReceipt = () => {
        if (
          [
            PAYMENT_STATUSES.APPROVED_BY_PROVIDER,
            PAYMENT_STATUSES.DISBURSED,
          ].includes(paymentStatus) &&
          ![LOAN_PAYMENT_METHOD].includes(paymentMethod)
        ) {
          return true;
        }
      };
      const canDownloadTaxInvoice = () =>{
        if (
          [
            PAYMENT_STATUSES.APPROVED_BY_PROVIDER,
            PAYMENT_STATUSES.DISBURSED,
          ].includes(paymentStatus))
          {
            return true
          }
      }
      return {
        amount: INR_SYMBOL + paymentDetail.amount.toLocaleString("en-IN"),
        status: paymentDetail.status.name,
        paymentDate: new Date(paymentDetail.date).toLocaleString(),
        paymentMethod: paymentDetail.method.name,
        receipt: canDownloadReceipt() ? (
          <PrimaryButton
            onClick={() =>
              handleOnClickDownloadPaymentReceipt(
                paymentDetail.id,
                fetchPaymentReceipt,
                openSnackbar
              )
            }
            disabled={isFetchPaymentReceiptLoading}
          >
            {isFetchPaymentReceiptLoading ? <Loader /> : "Download"}
          </PrimaryButton>
        ) : (
          "N/A"
        ),
        taxInvoice: canDownloadTaxInvoice() ? (
          <PrimaryButton
            onClick={handleDialogOpen(
              setOpenDialogContent,
              <DownloadInvoice
                onClose={handleDialogClose(setOpenDialogContent)}
                paymentId={paymentDetail.id}
              />,
              { width: "50rem", height: "20rem" }
            )}
          >
            Download
          </PrimaryButton>
        ) : (
          "N/A"
        ),

        history: Array.isArray(
          paymentDetail.transaction_details?.payment_attempts
        )
          ? paymentDetail.transaction_details.payment_attempts.map(
              (attempt) => ({
                date: new Date(attempt.date).toLocaleString(),
                method: attempt.method?.name || "N/A",
                status: attempt.status?.name || "N/A",
                reason: attempt.reason?.name || "N/A",
                description: attempt.error?.description ? (
                  <Tooltip title={attempt.error.description} arrow>
                    <span>
                      {attempt.error.description.length > 50
                        ? `${attempt.error.description.slice(0, 50)}...`
                        : attempt.error.description}
                    </span>
                  </Tooltip>
                ) : (
                  "N/A"
                ),
              })
            )
          : [],
      };
    });
};

const handleClickGetOrderSummaryPDFButton = (
  orderId,
  refetchOrderSummaryPDF,
  openSnackbar
) => {
  if (!!orderId) {
    refetchOrderSummaryPDF()
      .then((response) => {
        if (response && response.status === "success") {
          const order_summary_response = response.data;
          if (!!order_summary_response.message) {
            openSnackbar(order_summary_response.message);
          }
          if (!!order_summary_response.signed_url) {
            const link = document.createElement("a");
            link.href = order_summary_response.signed_url;
            link.download = `order_summary_${orderId}.pdf`;
            link.click();
            openSnackbar("Order summary downloaded successfully!", "success");
          }
        } else {
          openSnackbar(
            response.error.message || "Error fetching Order Summary"
          );
        }
      })
      .catch((error) => {
        openSnackbar("Error fetching Order Summary");
      });
  }
};

const handleClickGetTaxInvoiceButton = (
  orderId,
  fetchTaxInvoicePDF,
  openSnackbar
) => {
  if (orderId) {
    fetchTaxInvoicePDF(orderId)
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.signedUrl;
        link.click();
        openSnackbar("Tax Invoice downloaded successfully!", "success");
      })
      .catch((error) => {
        console.error("Error fetching tax invoice", error);
        openSnackbar(error.message || "Error fetching tax invoice", "error");
      });
  } else {
    console.error("No order ID provided");
  }
};

const handleClickProceedToPaymentButton = (selectedOrderID) => {
  const paymentPageUrl = `/paymentPreference?order_id=${selectedOrderID}`;
  window.location.href = paymentPageUrl;
  return;
};

const handleClickTransferOrder = (
  navigate,
  uamId,
  selectedOrder,
  selectedBusinessVerticalId
) => {
  navigate(
    `/punchOrder?uamId=${uamId}&verticalId=${selectedBusinessVerticalId}`,
    {
      state: { transferOrderDetails: selectedOrder },
    }
  );
};

const canDiscardOrder = (selectedOrder) => selectedOrder.amount_paid === 0;

const canRequestOrderCancellation = (selectedOrder) => {
  if (selectedOrder.amount_paid === 0) {
    return false;
  }
  return true;
};

const handleClickEditPaymentsButton = (selectedOrder) => {
  const newUrl = `/editPayments?order_id=${selectedOrder.order_id}`;
  window.location.href = newUrl;
  return;
};

const canRequestTransferOrder = (orderDetails, selectedOrder) => {
  if (selectedOrder.statusId === ORDER_STATUSES.FULFILLED) {
    return true;
  }

  if (selectedOrder.amount_paid === 0) {
    return false;
  }

  if (orderDetails && orderDetails.length === 0) {
    return true;
  }
  const getPendingOrders = (orderDetails) => {
    return orderDetails.filter((order) =>
      PENDING_ORDER_STATUS.includes(order.status.id)
    );
  };
  const pendingOrders = getPendingOrders(orderDetails);

  if (pendingOrders.length === 0) {
    return true;
  }

  if (pendingOrders > 1) {
    return false;
  }

  if (pendingOrders[0].order_id !== selectedOrder.order_id) {
    return false;
  }

  return true;
};

const hasPaymentAttemptDetails = (paymentDetail) => {
  return (
    paymentDetail.transaction_details &&
    Array.isArray(paymentDetail.transaction_details.payment_attempts) &&
    paymentDetail.transaction_details.payment_attempts.length > 0
  );
};

const CustomerOrderDetails = (props) => {
  const {
    orderId,
    orderDetails,
    selectedOrder,
    setSelectedOrderID,
    setSelectedOrder,
    orderStatus,
    uamId,
  } = props;
  const [searchParams] = useSearchParams();
  const { openSnackbar } = useSnackbarContext();
  const [openDialogContent, setOpenDialogContent] = useState(null);
  const customerUAMId = searchParams.get("uamId");
  const selectedBusinessVerticalId = searchParams.get("verticalId");
  const navigate = useNavigate();

  const {
    isLoading: isOrderSummaryPDFLoading,
    refetch: refetchOrderSummaryPDF,
  } = useFetchOrderSummaryPDF(orderId);

  const { fetchTaxInvoicePDF, isLoading: isTaxInvoicePDFLoading } =
    useFetchTaxInvoicePDF();

  const { fetchPaymentReceipt, isLoading: isFetchPaymentReceiptLoading } =
    useFetchPaymentReceipt();

  const packageDetails = selectedOrder
    ? getFormattedPackageDetails(selectedOrder)
    : [];

  const paymentDetailsRows = selectedOrder
    ? getFormattedPaymentDetails(
        selectedOrder,
        fetchPaymentReceipt,
        isFetchPaymentReceiptLoading,
        openSnackbar,
        setOpenDialogContent
      )
    : [];

  const refundDetailsRows = selectedOrder
    ? getFormattedRefundDetails(selectedOrder.refund_details)
    : [];

  const shouldShowAccordion = selectedOrder?.payment_details?.some(
    (paymentDetail) => hasPaymentAttemptDetails(paymentDetail)
  );

  const partPaymentDetails =
    selectedOrder && selectedOrder.part_payment_details
      ? getFormattedPartPaymentDetails(selectedOrder.part_payment_details)
      : [];

  const customerDetails =
    selectedOrder && selectedOrder.address_details
      ? selectedOrder.address_details
      : null;

  const hasPaymentHead = packageDetails.some((detail) => detail.hasPaymentHead);

  const paymentDetailsColumns = hasPaymentHead
    ? [...PAYMENT_DETAILS_COLUMN, { label: "Tax Invoice", field: "taxInvoice" }]
    : PAYMENT_DETAILS_COLUMN;

  return (
    <div className={styles.customerOrderDetailsContainer}>
      <div className={styles.ordersListAndCourseEnrollContainer}>
        <div className={styles.ordersList}>
          <div className={styles.contentTitle}>Customer Orders</div>
          {orderDetails
            ?.sort(
              (o1, o2) =>
                new Date(o2.order_created_date) -
                new Date(o1.order_created_date)
            )
            .map((order) => {
              const isSelected = selectedOrder?.order_id === order.order_id;
              return (
                <div
                  className={`${styles.orderBox} ${
                    isSelected ? styles.selectedOrder : ""
                  }`}
                  key={order.order_id}
                  onClick={() => {
                    if (!isSelected) {
                      setSelectedOrder(order);
                      setSelectedOrderID(order.order_id);
                    }
                  }}
                  style={{ cursor: isSelected ? "default" : "pointer" }}
                >
                  <DetailDisplay
                    title="Order Amount"
                    value={INR_SYMBOL + order.amount.toLocaleString("en-IN")}
                  />
                  <DetailDisplay
                    title="Punch Date"
                    value={new Date(
                      order.order_created_date
                    ).toLocaleDateString()}
                  />
                  <DetailDisplay
                    title="Status"
                    value={
                      order.status.name === "FULFILLED"
                        ? "FULLY_PAID"
                        : order.status.name
                    }
                  />
                </div>
              );
            })}
        </div>
        {orderDetails &&
          orderDetails.length > 0 &&
          selectedOrder?.business_unit_id !== KH_VERTICAL_ID && (
            <CourseEnroll uam_id={uamId} />
          )}
      </div>
      <div className={styles.customerOrderDetails}>
        <div className={styles.buttonsAndTitleContainer}>
          <div className={styles.contentTitle}>Order Details</div>
          <div className={styles.buttonContainer}>
            {" "}
            {orderStatus === ORDER_STATUSES.ACCEPTED &&
              canDiscardOrder(selectedOrder) && (
                <ErrorButton
                  onClick={handleDialogOpen(
                    setOpenDialogContent,
                    <DiscardOrder
                      onClose={handleDialogClose(setOpenDialogContent)}
                      orderId={selectedOrder.order_id}
                      setSelectedOrderID={setSelectedOrderID}
                    />,
                    { width: "25rem", height: "10rem" }
                  )}
                >
                  Discard Order
                </ErrorButton>
              )}
            {orderStatus === ORDER_STATUSES.ACCEPTED &&
              canRequestOrderCancellation(selectedOrder) && (
                <ErrorButton
                  onClick={handleDialogOpen(
                    setOpenDialogContent,
                    <CancelOrder
                      orderId={selectedOrder.order_id}
                      onClose={handleDialogClose(setOpenDialogContent)}
                    />,
                    { width: "25rem", height: "12rem" }
                  )}
                >
                  Request Cancel Order
                </ErrorButton>
              )}
            <PrimaryButton
              disabled={isOrderSummaryPDFLoading}
              onClick={() =>
                handleClickGetOrderSummaryPDFButton(
                  selectedOrder.order_id,
                  refetchOrderSummaryPDF,
                  openSnackbar
                )
              }
            >
              Get Order Summary
            </PrimaryButton>
            {orderStatus === ORDER_STATUSES.FULFILLED && !hasPaymentHead && (
              <PrimaryButton
                disabled={isTaxInvoicePDFLoading}
                onClick={() =>
                  handleClickGetTaxInvoiceButton(
                    selectedOrder.order_id,
                    fetchTaxInvoicePDF,
                    openSnackbar
                  )
                }
              >
                Get Tax Invoice
              </PrimaryButton>
            )}
            {(orderStatus === ORDER_STATUSES.ACCEPTED ||
              orderStatus === ORDER_STATUSES.FULFILLED) &&
              canRequestTransferOrder(orderDetails, selectedOrder) && (
                <PrimaryButton
                  onClick={() =>
                    handleClickTransferOrder(
                      navigate,
                      customerUAMId,
                      selectedOrder,
                      selectedBusinessVerticalId
                    )
                  }
                >
                  Request Modification
                </PrimaryButton>
              )}
          </div>
        </div>
        <div className={styles.customerDetails}>
          {selectedOrder &&
            selectedOrder.business_unit_id === BUSINESS_VERTICALS.KH &&
            selectedOrder.application_number && (
              <div>
                <span className={styles.title}>Application No.:</span>
                <span className={styles.value}>
                  {selectedOrder.application_number}
                </span>
              </div>
            )}
          <div>
            <span className={styles.title}>Billing Name:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.recipient_name}
            </span>
          </div>
          <div>
            <span className={styles.title}>Billing Phone No:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.masked_phone_no}
            </span>
          </div>
          <div>
            <span className={styles.title}>City:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.city}
            </span>
          </div>

          <div>
            <span className={styles.title}>State:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.state}
            </span>
          </div>
          <div>
            <span className={styles.title}>Country:</span>
            <span className={styles.value}>{DEFAULT_COUNTRY_NAME}</span>
          </div>
        </div>
        <div className={styles.packageDetail}>
          <Table columns={PACKAGE_DETAILS_COLUMN} rows={packageDetails} />
          {selectedOrder && (
            <div className={styles.amountDetails}>
              <div className={styles.detailItem}>
                <span className={styles.title}>Total MRP of Packages:</span>
                <span>
                  {INR_SYMBOL +
                    selectedOrder.listed_order_amount.toLocaleString("en-IN")}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Discount Amount:</span>
                <span>
                  {INR_SYMBOL +
                    selectedOrder.total_inr_discount_amount.toLocaleString(
                      "en-IN"
                    )}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Order Amount:</span>
                <span>
                  {INR_SYMBOL + selectedOrder.amount.toLocaleString("en-IN")}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Total Paid:</span>
                <span>
                  {INR_SYMBOL +
                    selectedOrder.amount_paid.toLocaleString("en-IN")}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Total Pending:</span>
                <span>
                  {INR_SYMBOL +
                    (
                      selectedOrder.amount - selectedOrder.amount_paid
                    ).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}
          <div className={styles.noteContainer}>
            {selectedOrder?.note && (
              <div className={styles.displayNoteContainer}>
                <div className={styles.noteIconAndTitle}>
                  <EventNoteIcon />
                  <Text>Note</Text>
                </div>
                <div className={styles.note}>{selectedOrder.note}</div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.amountDetailsTable}>
          <div className={styles.buttonsAndTitleContainer}>
            <div className={styles.contentTitle}>Payment Details</div>
            <div className={styles.paymentButtons}>
              <div className={styles.proceedToCollectPaymentButton}>
                <PrimaryButton
                  onClick={() => handleClickEditPaymentsButton(selectedOrder)}
                >
                  Edit Payments
                </PrimaryButton>
              </div>
              <div className={styles.proceedToCollectPaymentButton}>
                {orderStatus === ORDER_STATUSES.ACCEPTED && (
                  <PrimaryButton
                    onClick={() =>
                      handleClickProceedToPaymentButton(selectedOrder.order_id)
                    }
                  >
                    Proceed to Collect Payment
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
          {paymentDetailsRows.length > 0 ? (
            <div className={styles.tableContainer}>
              <Table
                columns={paymentDetailsColumns}
                rows={paymentDetailsRows}
                accordion={shouldShowAccordion}
                accordionTableColumns={PAYMENTS_ATTEMPTS_COLUMN}
                maxHeight="13rem"
              />
            </div>
          ) : (
            <ErrorMessage>No payments done.</ErrorMessage>
          )}
        </div>
        {refundDetailsRows.length > 0 ? (
          <div className={styles.tableContainer}>
            <div className={styles.contentTitle}>Refund Details</div>
            <Table
              columns={REFUND_DETAILS_COLUMN}
              rows={refundDetailsRows}
              maxHeight="10rem"
            />
          </div>
        ) : null}
        {partPaymentDetails.length > 0 ? (
          <div className={styles.tableContainer}>
            <div className={styles.contentTitle}>Part Payment Details</div>
            <Table
              columns={PART_PAYMENT_DETAILS_COLUMN}
              rows={partPaymentDetails}
              maxHeight="10rem"
            />
          </div>
        ) : null}
      </div>
      <Dialog
        open={openDialogContent !== null}
        handleClose={handleDialogClose(setOpenDialogContent)}
        width={openDialogContent?.size?.width}
        height={openDialogContent?.size?.height}
      >
        {openDialogContent?.content}
      </Dialog>
    </div>
  );
};
export default CustomerOrderDetails;
